import React, { useState, useEffect, useCallback, memo } from 'react';
import { JiraWorkspaceAri } from '@atlassian/ari/jira/workspace';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import useUserPickerPermission from '../services/user-picker-permission/index.tsx';
import type { Props } from '../common/types.tsx';
import { useMaybeCanSeeExtendedShareDialog } from '../common/hooks/use-maybe-can-see-extended-share-dialog.tsx';
import { useIsSplitShareButtonEnabled } from '../common/hooks/use-is-split-share-button-enabled.tsx';
import { getAccessLevelIcon } from '../common/utils/access-level-icon.tsx';
import messages from './messages.tsx';
import ShareButtonView from './share-view/index.tsx';

export const JiraShareButton = memo((props: Props) => {
	const {
		triggerButtonStyle,
		triggerButtonTooltipPosition,
		onDialogClose,
		renderCustomTriggerButton,
		customTriggerButtonIcon,
		shareFormTitle,
		shareContentType,
		poSpotlightShareButtonId,
		additionalTabs,
		shareIntegrations,
		onTriggerButtonClick,
		shareShortcutTriggerCount = 0,
		prevShareShortcutTriggerCount = 0,
		...otherProps
	} = props;

	const { formatMessage } = useIntl();
	const { data: orgId } = useOrgId();
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const [{ data: hasUserPermission, error: userPermissionError }, fetchUserPermission] =
		useUserPickerPermission();
	const [isAutoOpen, setIsAutoOpen] = useState<boolean>(false);

	const maybeExtendedShareDialogEnabled = useMaybeCanSeeExtendedShareDialog();
	const isSplitShareButtonEnabled = useIsSplitShareButtonEnabled();
	const { data: projectContext } = useProjectContext();

	const shortcutPressed = !!(
		shareShortcutTriggerCount &&
		shareShortcutTriggerCount > 0 &&
		shareShortcutTriggerCount > prevShareShortcutTriggerCount
	);

	const onTriggerButtonClickHandler = useCallback(() => {
		// TODO: move fetchUserPermission to a hook in @atlassian/jira-share-dialog
		// jira/src/packages/platform/ui/share-dialog/src/common/utils/useShareConfig.tsx
		// https://hello.jira.atlassian.cloud/browse/WAR-3718
		fetchUserPermission();
		onTriggerButtonClick?.();
	}, [fetchUserPermission, onTriggerButtonClick]);

	const getCustomFormTitle = useCallback(() => {
		switch (shareContentType) {
			case 'summary':
				return formatMessage(messages.shareMainTabTextJwmSummary);
			case 'board':
				return formatMessage(messages.shareMainTabTextJwmBoard);
			case 'list':
				return formatMessage(messages.shareMainTabTextJwmList);
			case 'calendar':
				return formatMessage(messages.shareMainTabTextJwmCalendar);
			case 'timeline':
				return formatMessage(messages.shareMainTabTextJwmTimeline);
			case 'form':
				return formatMessage(messages.shareMainTabTextJwmForm);
			case 'attachments':
				return formatMessage(messages.shareMainTabTextJwmAttachments);
			default:
				return shareFormTitle;
		}
	}, [formatMessage, shareContentType, shareFormTitle]);

	useEffect(() => {
		if (userPermissionError) {
			fireErrorAnalytics({
				meta: {
					id: 'userPermissionError',
					packageName: 'jiraShareButton',
					teamName: 'jira-warepil',
				},
			});
		}
	}, [userPermissionError]);

	useEffect(() => {
		if (shortcutPressed) {
			setIsAutoOpen(true);
		}
	}, [shortcutPressed, shareShortcutTriggerCount]);

	return (
		<ShareButtonView
			{...(projectContext && {
				key: `${projectContext.projectKey}-${projectContext.accessLevel ?? ''}`,
			})}
			triggerButtonAppearance="default"
			isAutoOpenDialog={isAutoOpen}
			renderCustomTriggerButton={renderCustomTriggerButton}
			shareContentType={shareContentType}
			shareFormTitle={shareFormTitle || getCustomFormTitle()}
			triggerButtonStyle={triggerButtonStyle}
			isBrowseUsersEnabled={hasUserPermission}
			onDialogClose={() => {
				setIsAutoOpen(false);
				onDialogClose?.();
			}}
			orgId={orgId}
			workspaceAri={JiraWorkspaceAri.create({ activationId, siteId: cloudId }).toString()}
			additionalTabs={additionalTabs}
			shareIntegrations={shareIntegrations}
			{...(fg('plan-timeline-share-tooltip-position') && {
				triggerButtonTooltipPosition,
			})}
			{...(isSplitShareButtonEnabled && {
				customTriggerButtonIcon: getAccessLevelIcon(projectContext?.accessLevel),
			})}
			maybeExtendedShareDialogEnabled={maybeExtendedShareDialogEnabled}
			onTriggerButtonClick={onTriggerButtonClickHandler}
			{...otherProps}
		/>
	);
});

// TODO: remove default export and move the comopnent to index.tsx on move_share_dialog_to_jira cleanup
export default JiraShareButton;
